import { FC, ReactNode } from 'react';

import { Box } from '@mui/material';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { useBreakpoints } from '@/hooks/useBreakpoints';

import { darkGradientContainerSx } from '../shared/constants';
import { OnboardingImageScreen } from './components/OnboardingImageScreen';

const reCaptchaKey: string = import.meta.env.VITE_REACT_RE_CAPTCHA_KEY;

interface OnboardingLayoutProps {
  children: ReactNode;
}

/**
 * Specialized layout for onboarding flows (login, registration, etc.).
 *
 * Features:
 * - Dark gradient background styling
 * - Split-screen layout on desktop (form left, preview right)
 * - Single column on mobile
 * - Integrated Google ReCAPTCHA v3
 * - Responsive padding and spacing
 */
export const OnboardingLayout: FC<OnboardingLayoutProps> = ({ children }) => {
  const { isDesktop } = useBreakpoints();

  return (
    <GoogleReCaptchaProvider
      container={{
        element: 'onboarding-recaptcha',
        parameters: {
          badge: 'bottomleft',
          theme: 'dark'
        }
      }}
      reCaptchaKey={reCaptchaKey}
    >
      <Box sx={darkGradientContainerSx}>
        <Box
          sx={{
            minHeight: '100vh',
            minWidth: '300px',
            display: 'flex',
            justifyContent: 'center',
            flex: 1,
            position: 'relative'
          }}
        >
          <Box
            sx={{
              flex: 1
            }}
          >
            <Box
              sx={{
                py: '8%',
                pl: 4,
                pr: {
                  xs: 0,
                  md: 4
                }
              }}
            >
              {children}
            </Box>
          </Box>
          {isDesktop && <OnboardingImageScreen />}
        </Box>
      </Box>
    </GoogleReCaptchaProvider>
  );
};
